import { useCallback, useEffect, useRef, useState } from 'react';

import useDebounce from '@picsart/growth-rc/useDebounce';

import { setAppStore } from '@/store';
import useRecentSearches from '@/hooks/useRecentSearches';
import useSearchQuery from '@/hooks/queries/useSearchQuery';
import { usePreset } from '@/hooks/useLibrary';
import { SEARCH_DEBOUNCE_MS } from '@/consts/search';
import formatSearchKeyword from '@/utils/format/formatSearchKeyword';
import { muteCurrentVideo } from '@/utils/media/videoMuteCoordinator';
import closeSearch from '@/utils/search/closeSearch';
import sendSearchAnalytics from '@/utils/analytics/sendSearchAnalytics';
import getCreditsBalance from '@/utils/analytics/getCreditsBalance';
import trackPresetCredits from '@/utils/analytics/trackPresetCredits';
import { AiGenScreenAction } from '@/types/analytics';

import type { SearchResultItem } from '@/types';

// Stable empty fallback, so `items` keeps its identity between renders while
// there is no data and the handlers that depend on it stay memoized.
const NO_ITEMS: SearchResultItem[] = [];

/**
 * Shared search state for both surfaces — the mobile screen and the desktop
 * dropdown differ only in how they present it.
 *
 * Results follow typing on both: `value` is what's typed, `query` is the
 * normalized term that reaches React Query, and the debounce between them
 * collapses a burst of keystrokes into one request. An explicit submit, a
 * clear, or a recent-search tap commits immediately and skips the wait.
 */
const useSearch = () => {
  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');
  const { loadPreset } = usePreset();
  const { recentSearches, remember, forget, forgetAll } = useRecentSearches();

  const debounce = useDebounce(SEARCH_DEBOUNCE_MS);

  // The debounced callback reads the term through a ref rather than closing
  // over it, so an immediate commit (clear, submit, recent tap) can't be
  // overwritten by a timer that was already in flight — it fires against the
  // latest term and lands on the same value.
  const latestTermRef = useRef('');

  const commit = useCallback((term: string) => {
    latestTermRef.current = term;
    setQuery(term);
  }, []);

  const { data, isFetching, isError, refetch } = useSearchQuery(query);
  const items = data?.items ?? NO_ITEMS;

  // Terms already reported as `search_result_seen`. Every settled query is reported
  // once — including the prefixes the debounce lets through, which is what the
  // user actually searched — but a cache hit, a refetch, or a re-render on the
  // same term must not count again.
  const reportedTermsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!query || isFetching || isError || !data) return;
    if (reportedTermsRef.current.has(query)) return;

    reportedTermsRef.current.add(query);
    sendSearchAnalytics({
      action: AiGenScreenAction.SEARCH_RESULT_SEEN,
      keyword: query,
      settings: { results_count: data.items.length },
    });
  }, [query, isFetching, isError, data]);

  const handleChange = useCallback(
    (nextValue: string) => {
      setValue(nextValue);
      latestTermRef.current = formatSearchKeyword(nextValue);
      debounce(() => setQuery(latestTermRef.current));
    },
    [debounce]
  );

  // Recording every keystroke would fill the list with prefixes, so a term is
  // only remembered once the user commits to it — an explicit submit, or
  // opening one of its results.
  const handleSubmit = useCallback(() => {
    const term = formatSearchKeyword(value);
    commit(term);
    remember(term);

    // Deliberate intent, as opposed to the `search_result_seen` that every settled
    // keystroke burst also reports.
    if (term) {
      sendSearchAnalytics({
        action: AiGenScreenAction.SEARCH_SUBMIT,
        keyword: term,
      });
    }
  }, [value, commit, remember]);

  const handleClear = useCallback(() => {
    setValue('');
    commit('');
  }, [commit]);

  const handleRecentSelect = useCallback(
    (term: string) => {
      setValue(term);
      // Committed rather than debounced — a tap on a past search shouldn't
      // sit through the wait.
      commit(term);
      remember(term);
      sendSearchAnalytics({
        action: AiGenScreenAction.SEARCH_RECENT_CLICK,
        keyword: term,
      });
    },
    [commit, remember]
  );

  const handleResultClick = useCallback(
    (item: SearchResultItem) => {
      // Results with no originating preset can't open a feed — they render
      // non-interactive, so this is only a belt-and-braces guard.
      if (!item.presetSlug) return;

      muteCurrentVideo();
      remember(query);

      // The only click event for a search result — deliberately not the generic
      // `click` + `item: effect` a preset card sends, which would double-count
      // the same tap under `chooser_screen`.
      sendSearchAnalytics({
        action: AiGenScreenAction.SEARCH_RESULT_CLICK,
        keyword: query,
        settings: {
          ...(item.categorySlug && { category: item.categorySlug }),
          id: item.presetSlug,
          position: items.findIndex(({ id }) => id === item.id) + 1,
          credits_balance: getCreditsBalance(),
        },
      });
      trackPresetCredits({
        slug: item.presetSlug,
        category: item.categorySlug ?? '',
      });

      // `activeTabSlug` is not incidental here: the feed builds its slides
      // from that one section (`usePresetFeedData`), so without it the entry
      // slug is never found and every result opens at index 0 of whatever
      // category happened to be active.
      //
      // Search stays open on purpose — `SearchScreen` hides itself while the
      // feed is up and comes back when it closes, so results and scroll
      // survive the round trip.
      setAppStore({
        ...(item.categorySlug && { activeTabSlug: item.categorySlug }),
        presetFeedEntrySlug: item.presetSlug,
      });
      loadPreset(item.presetSlug);
    },
    [loadPreset, remember, query, items]
  );

  const handleRetry = useCallback(() => {
    refetch().catch(() => {
      // React Query keeps the error state; the button stays available.
    });
  }, [refetch]);

  return {
    value,
    query,
    items,
    recentSearches,
    restrictionMessage: data?.restrictionMessage ?? null,
    isFetching,
    isError,
    // A query that returned nothing — distinct from the idle state before
    // anything was searched, which offers recent searches instead.
    hasNoResults: Boolean(query) && !isFetching && !isError && !items.length,
    handleChange,
    handleSubmit,
    handleClear,
    // Shared with the popstate path, so both report one `search_close`.
    handleClose: closeSearch,
    handleResultClick,
    handleRecentSelect,
    handleRecentRemove: forget,
    handleRecentClearAll: forgetAll,
    handleRetry,
  };
};

export default useSearch;
